import { useCurrentUser } from '@/hooks/dataHelpers';
import { getFromLS, setToLS } from '@/lib/commonHelpers';
import { selectEmbedded, useCommonStore } from '@/store/commonStore';
import { selectExperience, useExperienceStore } from '@/store/experienceStore';
import { useOne } from '@refinedev/core';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

interface UseGetExperienceProps {
  id?: string;
  experienceFromProps?: any;
  enabled?: boolean;
}

interface UseGetExperienceReturn {
  experience: any;
  isLoading: boolean;
  error: any;
  experienceData: any;
}

export const useExperienceOne = ({
  id,
  experienceFromProps,
  enabled = true,
}: UseGetExperienceProps): UseGetExperienceReturn => {
  // Get embedded state from common store
  const embedded = useCommonStore(selectEmbedded);

  const currentUser = useCurrentUser();
  // NOTE: Always use the experience from the store as we are not updating refine experience cache on custom update
  const experience = useExperienceStore(selectExperience);
  const { setExperience } = useExperienceStore();

  // Local state for loading management
  const [isLoading, setIsLoading] = useState(true);
  const hasHydratedExperience = useRef(false);

  // When the assessment is opened from a room (e.g. the room's experience cards),
  // the URL carries `?room_uuid=`, which we forward to the BE so the experiences#show
  // preprocessor can grant room invigilators access.
  // We persist it per-assessment and per-user in localStorage so it survives in-app
  // navigation and refresh without depending on the URL: read it from the URL on first open,
  // otherwise fall back to the stored value.
  const [searchParams] = useSearchParams();
  const [roomUuid] = useState<string | null>(() => {
    const userId = currentUser?.id ?? '';
    const storageKey = `assessment_room_uuid_${userId}_${id ?? ''}`;
    const fromUrl = searchParams.get('room_uuid');
    if (fromUrl) {
      setToLS(storageKey, fromUrl);
      return fromUrl;
    }
    return getFromLS(storageKey);
  });

  // Note: Secure Enclave signing intentionally NOT wired here. The BE
  // does not mount `verify_signed_submission!` on `experiences#show`,
  // so any header we send is ignored and the per-mount nonce + native
  // bridge call would be wasted. The verified GET for the student-take
  // flow is `experiences#get_topics_and_segments`, signed by
  // `ExperienceTake.tsx`. If we ever start verifying on `show`, wire
  // signing here following the empty-payload pattern (GETs hash empty
  // bytes on the BE; see `signed_submission.rb` and the comment block
  // in `ExperienceTake.tsx` near the topics-and-segments useCustom).

  // Fetch experience data using Refine's useOne hook
  const {
    data,
    error: experienceErrorFromOne,
    isLoading: queryLoading,
  } = useOne({
    resource: 'experiences',
    id: id,
    meta: {
      returnNestedData: true,
      query: {
        with_nested_children: true,
        ...(roomUuid ? { room_uuid: roomUuid } : {}),
      },
    },
    queryOptions: {
      enabled: enabled && !embedded && !!id,
    },
  });

  // Determine which experience data to use
  const experienceFromUseOne = data?.data;
  const experienceData: any = embedded ? experienceFromProps : experienceFromUseOne;
  const experienceDataError = experienceData?.error || experienceErrorFromOne;

  // Handle experience hydration — v3 parity (APL-12281): seed the store
  // unconditionally from the latest emission, same as v3's hook. The id-reset
  // effect below re-opens the latch right after mount, so the refetch that
  // follows a route-leave invalidation re-seeds the store — that is how the
  // post-publish `experience_user` reaches the take view. Trade-off accepted
  // with this parity: a remount over a stale cache overwrites store-only
  // patches (e.g. deliver autosave) until the refetch lands — the same
  // behaviour v3 shipped.
  useEffect(() => {
    if (hasHydratedExperience.current) return;

    if (experienceData) {
      setExperience(experienceData);
      setIsLoading(false);
      hasHydratedExperience.current = true;
    } else if (embedded && !experienceFromProps) {
      // If embedded but no experienceFromProps, keep loading
      setIsLoading(true);
    } else if (!embedded && !queryLoading && !experienceFromUseOne) {
      // If not embedded and query finished without data, stop loading
      setIsLoading(false);
    }
  }, [
    experienceData,
    embedded,
    experienceFromProps,
    queryLoading,
    experienceFromUseOne,
    setExperience,
  ]);

  // Reset hydration ref when ID changes
  useEffect(() => {
    if (!id) return;

    hasHydratedExperience.current = false;
    setIsLoading(true);
  }, [id]);

  // Handle loading state for non-embedded queries
  useEffect(() => {
    if (!embedded) {
      setIsLoading(queryLoading);
    }
  }, [queryLoading, embedded]);

  return {
    experience,
    isLoading,
    error: experienceDataError,
    experienceData,
  };
};
